import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { playSound } from '../../utils/soundFX'
import { trackEvent } from '../../utils/analytics'

// Parse a #rrggbb badge colour to its r,g,b parts (null if missing/invalid).
function hexToRgb(hex) {
  if (!hex) return null
  const m = hex.replace('#', '')
  if (m.length < 6) return null
  return {
    r: parseInt(m.slice(0, 2), 16),
    g: parseInt(m.slice(2, 4), 16),
    b: parseInt(m.slice(4, 6), 16),
  }
}

// Faded, dark-tinted panel background derived from the lightbox's badge colour:
// a low-alpha colour wash layered over the dark base, so a red stage reads as
// faded red and a blue sub-point as faded blue (falls back to plain dark).
function tintedPanelBackground(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return 'rgba(0, 0, 0, 0.85)'
  const { r, g, b } = rgb
  return `linear-gradient(rgba(${r}, ${g}, ${b}, 0.22), rgba(${r}, ${g}, ${b}, 0.22)), rgba(0, 0, 0, 0.85)`
}

// Accent CSS variables for the lightbox's interactive controls (close button,
// carousel nav buttons, dots), so they pick up the badge colour: red for stages,
// blue for sub-points. Falls back to the brand-red tokens when no badge colour.
function accentVars(hex) {
  // Default to brand red when the lightbox has no badge colour (e.g. crew photos),
  // so its carousel bar / controls get the same tint as the mission-profile ones
  // instead of falling back to the dark base.
  const rgb = hexToRgb(hex) ?? { r: 238, g: 58, b: 23 }
  const { r, g, b } = rgb
  return {
    '--lightbox-accent': `rgb(${r}, ${g}, ${b})`,
    '--lightbox-accent-faded': `rgba(${r}, ${g}, ${b}, 0.3)`,
    '--lightbox-accent-border': `rgba(${r}, ${g}, ${b}, 0.6)`,
    // Carousel control-bar bg — same dark-tinted wash as the panel background,
    // so the bar reads as a darker faded red/blue rather than a bright fill.
    '--lightbox-accent-bar': `linear-gradient(rgba(${r}, ${g}, ${b}, 0.22), rgba(${r}, ${g}, ${b}, 0.22)), rgba(0, 0, 0, 0.85)`,
  }
}

// Stable per-image identifier derived from the slide's src filename (slides have
// no explicit id in artemisMissionStages.json, but every src path is unique).
// e.g. "/images/artemis/poi/01/01_A-Crew.webp" -> "01_A-Crew"
function imageIdFromSrc(src) {
  if (!src) return null
  return src.split('/').pop().replace(/\.[^.]+$/, '')
}

function SlideBody({ body }) {
  if (!body?.length) return null
  return (
    <div className="artemis-lightbox__body">
      {body.map((block, i) => {
        if (block.type === 'paragraph') {
          return <p key={i} className="artemis-lightbox__caption">{block.text}</p>
        }
        if (block.type === 'bullets') {
          return (
            <ul key={i} className="artemis-lightbox__bullets">
              {block.items.map((item, j) => (
                <li key={j} className="artemis-lightbox__bullet">{item}</li>
              ))}
            </ul>
          )
        }
        return null
      })}
    </div>
  )
}

function SlideMedia({ slide, label, eager = false }) {
  if (!slide?.src) return null
  return slide.type === 'video'
    ? <video className="artemis-lightbox__media" src={slide.src} autoPlay loop playsInline muted />
    // lazy + async decode so the mobile-portrait branch (which stacks ALL slides)
    // doesn't decode every image at once — critical for the 10-photo crew gallery
    // on memory-constrained iOS, where decoding all at once leaves them blank.
    // The desktop/tablet filmstrip passes eager so the off-screen prev/next frames
    // are decoded and ready to slide into view (only three at a time, so it's cheap).
    : <img className="artemis-lightbox__media" src={slide.src} alt={label} draggable={false} loading={eager ? 'eager' : 'lazy'} decoding={eager ? 'auto' : 'async'} />
}

export function ArtemisMediaLightbox({ media, layerLabel, onClose, guided = false, onInteract, onAutoClose, isMobilePortrait = false, hideCloseButton = false, captionToggle = false }) {
  const [slideIndex, setSlideIndex] = useState(0)
  const [flickering, setFlickering] = useState(false)
  const [atScrollTop, setAtScrollTop] = useState(true)
  // When captionToggle is on (crew photos), the boxed top-right description is
  // hidden behind an "i" button; tapping toggles it per image. Keyed by src.
  const [openCaptions, setOpenCaptions] = useState(() => new Set())
  const scrollRef = useRef(null)
  const visitedRef = useRef(new Set([0]))
  // Liked images (by image id) — drives the heart fill. State so the heart
  // re-renders on toggle. Likes are not tracked per-toggle; they're aggregated
  // into the engagement event on lightbox close (see below). A ref mirror lets
  // that cleanup closure read the latest set without a stale capture (same
  // reason visitedRef exists).
  const [likedImages, setLikedImages] = useState(() => new Set())
  const likedImagesRef = useRef(new Set())
  useEffect(() => { likedImagesRef.current = likedImages }, [likedImages])

  const toggleLike = (src) => {
    const imageId = imageIdFromSrc(src)
    if (!imageId) return
    setLikedImages(prev => {
      const next = new Set(prev)
      if (next.has(imageId)) {
        next.delete(imageId)
      } else {
        next.add(imageId)
        playSound('DataPoint_Open')
      }
      return next
    })
    onInteract?.()
  }

  useEffect(() => {
    setSlideIndex(0)
    // Reset scroll state when a new lightbox opens — otherwise a previously
    // scrolled-down lightbox leaves atScrollTop false (top fade gradient showing)
    // and the scroll container parked mid-way, so the next one opens looking as
    // if it had already been scrolled. Snap back to the top and clear the flag.
    setAtScrollTop(true)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    // Clear likes when the lightbox closes/opens (media identity changes) so a
    // re-opened lightbox starts un-liked. This fires on any media change — close
    // (media -> null), reopen, or stage switch — but NOT on a slide change (that
    // only moves slideIndex), so likes still persist while flipping between images
    // in the same open lightbox. Runs in the SAME [media] pass as the analytics
    // effect below; React runs that effect's CLEANUP (which reads the old likes to
    // emit the engagement event) before this setup, so the reset never races the
    // event.
    setLikedImages(new Set())
  }, [media])

  // Touch swipe through the carousel as a filmstrip: the track holds [prev,
  // current, next] and rests centred on the current frame (translateX(-100%)).
  // During a horizontal drag the whole strip follows the finger, so the previous /
  // next image slides into frame alongside the current one. On release, a swipe
  // past ~33% of the frame width snaps to the neighbour and commits the new index;
  // anything less springs back. Vertical gestures pass through to scroll. The track
  // is keyed on slideIndex so committing a change re-renders the new prev/cur/next
  // and the track snaps back to centre transition-less.
  const swipeStartRef = useRef(null)
  const trackRef = useRef(null)
  const settleTimerRef = useRef(null)
  const SETTLE_MS = 250

  // Position the strip: base is -100% (current frame centred); `px` is the live
  // finger offset on top of that. `animate` toggles the snap/spring transition.
  const setTrackTransform = (px, animate) => {
    const el = trackRef.current
    if (!el) return
    el.style.transition = animate ? `transform ${SETTLE_MS}ms ease` : 'none'
    el.style.transform = `translateX(calc(-100% + ${px}px))`
  }

  // The track is keyed on slideIndex, so each render mounts it fresh at the resting
  // -100% (no inline transform). Clear any leftover settle timer on index change.
  useLayoutEffect(() => {
    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current)
        settleTimerRef.current = null
      }
    }
  }, [slideIndex])

  useEffect(() => () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current)
  }, [])

  const handleSwipeStart = (e) => {
    // If a previous swipe's settle is still pending, cancel it so a fast follow-up
    // drag doesn't commit a stale index mid-gesture.
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current)
      settleTimerRef.current = null
    }
    const t = e.touches[0]
    const now = e.timeStamp || performance.now()
    // Track the latest sample too, so velocity is measured over the final leg of the
    // gesture (a flick) rather than the whole press — a slow drag that ends in a
    // quick flick still registers as a flick.
    swipeStartRef.current = { x: t.clientX, y: t.clientY, dragging: false, width: 0, lastX: t.clientX, lastT: now, prevX: t.clientX, prevT: now }
  }

  const handleSwipeMove = (e) => {
    const start = swipeStartRef.current
    if (!start) return
    const slidesLen = media?.slides?.length ?? 1
    if (slidesLen < 2) return
    const t = e.touches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    // Lock to horizontal once the gesture is clearly sideways; let vertical
    // gestures scroll/pass through untouched.
    if (!start.dragging) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return
      start.dragging = true
      // Track is one frame wide (width:100% of the wrap), so its offsetWidth IS the
      // frame width — the -100% rest offset equals exactly this.
      start.width = trackRef.current ? trackRef.current.offsetWidth : 0
    }
    // Roll the velocity samples forward (prev = the one before the current sample).
    start.prevX = start.lastX
    start.prevT = start.lastT
    start.lastX = t.clientX
    start.lastT = e.timeStamp || performance.now()
    // Resistance at the ends — no neighbour to reveal, so damp the travel.
    const atStart = slideIndex === 0 && dx > 0
    const atEnd = slideIndex === slidesLen - 1 && dx < 0
    const eased = (atStart || atEnd) ? dx * 0.35 : dx
    setTrackTransform(eased, false)
  }

  const handleSwipeEnd = (e) => {
    const start = swipeStartRef.current
    swipeStartRef.current = null
    if (!start) { setTrackTransform(0, false); return }
    const t = e.changedTouches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    const slidesLen = media?.slides?.length ?? 1
    if (slidesLen < 2) { setTrackTransform(0, false); return }
    const frame = start.width || (trackRef.current ? trackRef.current.offsetWidth : 0)
    // Two ways to advance, whichever fires first:
    //  • Distance — dragged past a quarter of the frame width (40px floor).
    //  • Flick — a quick swipe, even if short. Velocity is px/ms over the final leg
    //    of the gesture; ~0.4 px/ms (≈400 px/s) with a small 12px minimum travel
    //    makes a light flick enough, without firing on an accidental tap-jitter.
    const horizontal = Math.abs(dx) >= Math.abs(dy)
    const endT = e.timeStamp || performance.now()
    const vx = (endT - start.prevT) > 0 ? (start.lastX - start.prevX) / (endT - start.prevT) : 0
    const distancePass = Math.abs(dx) >= Math.max(40, frame * 0.25)
    const flickPass = Math.abs(vx) >= 0.4 && Math.abs(dx) >= 12
    const passed = horizontal && (distancePass || flickPass)
    // Direction comes from the flick velocity when it's a flick (so a fast reverse
    // flick after a small drag goes the way you flicked), else from net travel.
    const dir = flickPass ? (vx < 0 ? -1 : 1) : (dx < 0 ? -1 : 1)
    const goNext = passed && dir < 0 && slideIndex < slidesLen - 1
    const goPrev = passed && dir > 0 && slideIndex > 0
    if (goNext || goPrev) {
      // Animate the strip the rest of the way onto the neighbour, THEN commit the
      // index. The neighbour we settle on is exactly the new centre frame, so the
      // keyed re-render (back at resting -100%) lands without a visible jump.
      playSound(goNext ? 'DataPoint_Open' : 'DataPoint_Close')
      setTrackTransform(goNext ? -frame : frame, true)
      onInteract?.()
      settleTimerRef.current = setTimeout(() => {
        settleTimerRef.current = null
        setSlideIndex(i => goNext ? Math.min(i + 1, slidesLen - 1) : Math.max(i - 1, 0))
      }, SETTLE_MS)
    } else {
      // under threshold or at an end → spring the strip back to centre
      setTrackTransform(0, true)
      if (start.dragging) onInteract?.()
    }
  }

  // Lightbox engagement — on close (media -> null, stage switch, or unmount) emit
  // how many unique slides the user viewed out of the total, plus how many of this
  // lightbox's images were liked and which ones. Fires for any lightbox with at least
  // one slide (single-slide reports 1/1). The event name reflects how the lightbox was
  // opened (guided playback vs manual marker click) so reporting can separate the two.
  useEffect(() => {
    if (!media) return
    visitedRef.current = new Set([0])
    const stage = media.label
    const stageId = media.stageId
    const slidesForMedia = media.slides ?? []
    const eventName = media.openMode === 'manual'
      ? 'artemis_lightbox_manual_engagement'
      : 'artemis_lightbox_guided_engagement'
    const total = slidesForMedia.length
    return () => {
      if (total < 1) return
      const viewed = visitedRef.current.size
      // Scope likes to this lightbox: intersect the session-global liked set with
      // this media's slide ids.
      const likedThis = slidesForMedia
        .map(s => imageIdFromSrc(s.src))
        .filter(id => id && likedImagesRef.current.has(id))
      trackEvent(eventName, {
        stage,
        stage_id: stageId,
        slides_viewed: viewed,
        slides_total: total,
        liked_count: likedThis.length,
        liked_images: likedThis.join(','),
      })
    }
  }, [media])

  useEffect(() => { if (media) visitedRef.current.add(slideIndex) }, [slideIndex, media])

  // Auto-advance slides every 60s in guided mode; auto-close after last slide
  useEffect(() => {
    if (!guided || !media) return
    const slides = media.slides ?? []
    const timer = setTimeout(() => {
      if (slideIndex < slides.length - 1) {
        setSlideIndex(i => i + 1)
      } else {
        onAutoClose?.()
      }
    }, 60000)
    return () => clearTimeout(timer)
  }, [guided, media, slideIndex, onAutoClose])

  useEffect(() => {
    if (!media) return
    setFlickering(true)
    const t = setTimeout(() => setFlickering(false), 600)
    return () => clearTimeout(t)
  }, [media])

  useEffect(() => {
    if (!media) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setSlideIndex(i => Math.min(i + 1, (media.slides?.length ?? 1) - 1))
      if (e.key === 'ArrowLeft') setSlideIndex(i => Math.max(i - 1, 0))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [media, onClose])

  if (!media) return null

  const slides = media.slides ?? []
  const slide = slides[slideIndex] ?? null
  const hasCarousel = slides.length > 1
  const flicker = flickering ? ' artemis-lightbox--text-flicker' : ''

  // Mobile portrait: full-screen single-column scroll, all slides stacked
  // Like button for a single image — shared by every slide on the mobile-portrait
  // stacked layout (desktop only needs one, for the currently-visible carousel
  // frame; mobile shows every slide at once so each gets its own).
  const renderLikeButton = (src) => {
    if (!src) return null
    const imageId = imageIdFromSrc(src)
    const liked = likedImages.has(imageId)
    return (
      <button
        type="button"
        className={`artemis-lightbox__like${liked ? ' artemis-lightbox__like--liked' : ''}`}
        onClick={() => toggleLike(src)}
        aria-pressed={liked}
        aria-label={liked ? 'Unlike image' : 'Like image'}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      </button>
    )
  }

  // The boxed top-right image description. With captionToggle (crew photos) it's
  // hidden behind an "i" button that toggles it open/closed per image; otherwise
  // the caption renders inline as before.
  const renderCaption = (slide) => {
    if (!slide?.caption) return null
    if (!captionToggle) {
      return <span className="artemis-lightbox__image-caption">{slide.caption}</span>
    }
    const key = slide.src || slide.caption
    const open = openCaptions.has(key)
    const toggleCaption = () => {
      playSound('DataPoint_Open')
      setOpenCaptions(prev => {
        const next = new Set(prev)
        next.has(key) ? next.delete(key) : next.add(key)
        return next
      })
    }
    return (
      <>
        <button
          type="button"
          className={`artemis-lightbox__info${open ? ' artemis-lightbox__info--open' : ''}`}
          onClick={toggleCaption}
          aria-pressed={open}
          aria-label={open ? 'Hide description' : 'Show description'}
        >
          <span aria-hidden="true">i</span>
        </button>
        {open && <span className="artemis-lightbox__image-caption">{slide.caption}</span>}
      </>
    )
  }

  if (isMobilePortrait) {
    return (
      <div
        className={`artemis-lightbox artemis-lightbox--mobile-portrait${atScrollTop ? ' artemis-lightbox--at-top' : ''}`}
        style={accentVars(media.badge?.color)}
      >
        {media.badge?.number && (
          <div
            className="artemis-lightbox__badge artemis-lightbox__badge--mobile"
            style={{
              backgroundColor: media.badge.color,
              borderRadius: media.badge.shape === 'diamond' ? '2px' : '50%',
              transform: media.badge.shape === 'diamond' ? 'rotate(45deg)' : 'none',
            }}
          >
            <span style={{ transform: media.badge.shape === 'diamond' ? 'rotate(-45deg)' : 'none' }}>
              {media.badge.number}
            </span>
          </div>
        )}
        <div
          className="artemis-lightbox__mobile-scroll"
          ref={scrollRef}
          onScroll={(e) => setAtScrollTop(e.currentTarget.scrollTop < 4)}
        >
          {/* First slide image */}
          {slides[0]?.src && (
            <div className="artemis-lightbox__media-wrap">
              <SlideMedia slide={slides[0]} label={media.label} />
              {renderLikeButton(slides[0].src)}
              {renderCaption(slides[0])}
              {slides[0].credit && (
                <div className="artemis-lightbox__image-meta">
                  <span className="artemis-lightbox__image-credit">Credit: {slides[0].credit}</span>
                </div>
              )}
            </div>
          )}
          {/* Title */}
          {media.label && <div className="artemis-lightbox__title">{media.label}</div>}
          {/* First slide body text */}
          <SlideBody body={slides[0]?.body} />
          {/* Remaining slides */}
          {slides.slice(1).map((s, i) => (
            s.src ? (
              <div key={i + 1} className="artemis-lightbox__mobile-extra-slide">
                <div className="artemis-lightbox__media-wrap">
                  <SlideMedia slide={s} label={media.label} />
                  {renderLikeButton(s.src)}
                  {renderCaption(s)}
                  {s.credit && (
                    <div className="artemis-lightbox__image-meta">
                      <span className="artemis-lightbox__image-credit">Credit: {s.credit}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : null
          ))}
        </div>
        {/* Fixed dismiss button. Hidden when the host already provides a dismiss
            control (e.g. the crew screen's NAV panel BACK→CLOSE), so there aren't
            two close buttons. */}
        {!hideCloseButton && (
          <div className="artemis-lightbox__mobile-footer">
            <button className="artemis-lightbox__close" onClick={() => { onClose() }}>
              <span>{guided ? 'CONTINUE' : 'CLOSE'}</span>
              <span className="artemis-lightbox__close-chevrons">[ ]</span>
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className="artemis-lightbox"
      style={accentVars(media.badge?.color)}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className="artemis-lightbox__panel-wrap">
        {media.badge?.number && (
          <div className="artemis-lightbox__badge-wrap">

            <div
              className="artemis-lightbox__badge"
              style={{
                backgroundColor: media.badge.color,
                borderRadius: media.badge.shape === 'diamond' ? '2px' : '50%',
                transform: media.badge.shape === 'diamond' ? 'rotate(45deg)' : 'none',
              }}
            >
              <span style={{ transform: media.badge.shape === 'diamond' ? 'rotate(-45deg)' : 'none' }}>
                {media.badge.number}
              </span>
            </div>
          </div>
        )}
        <div
          className="artemis-lightbox__panel"
          style={media.badge?.color
            ? { borderColor: media.badge.color, boxShadow: `0 0 30px ${media.badge.color}80`, background: tintedPanelBackground(media.badge.color) }
            : undefined}
        >

        {/* Left: media */}
        <div className="artemis-lightbox__left">
          <div
            className="artemis-lightbox__media-wrap"
            onTouchStart={handleSwipeStart}
            onTouchMove={handleSwipeMove}
            onTouchEnd={handleSwipeEnd}
          >
            {/* Filmstrip: [prev, current, next]. The track rests centred on the
                current frame (translateX(-100%)) and follows the finger on drag so
                the neighbouring image slides into view. Keyed on slideIndex so each
                committed change rebuilds the trio and re-centres. Empty placeholder
                frames at the ends keep the current image centred when there's no
                neighbour. */}
            <div
              key={slideIndex}
              ref={trackRef}
              className="artemis-lightbox__track"
            >
              <div className="artemis-lightbox__frame">
                {slideIndex > 0 && <SlideMedia slide={slides[slideIndex - 1]} label={media.label} eager />}
              </div>
              <div className="artemis-lightbox__frame">
                <SlideMedia slide={slide} label={media.label} eager />
              </div>
              <div className="artemis-lightbox__frame">
                {slideIndex < slides.length - 1 && <SlideMedia slide={slides[slideIndex + 1]} label={media.label} eager />}
              </div>
            </div>
            {renderLikeButton(slide?.src)}
            {renderCaption(slide)}
            {slide.credit && (
              <div className="artemis-lightbox__image-meta">
                <span className="artemis-lightbox__image-credit">Credit: {slide.credit}</span>
              </div>
            )}
            {hasCarousel && (
              <div className="artemis-lightbox__carousel artemis-lightbox__carousel--overlay">
                <button className="artemis-lightbox__nav-btn" onClick={() => { playSound('DataPoint_Close'); setSlideIndex(i => Math.max(i - 1, 0)); onInteract?.() }} disabled={slideIndex === 0} aria-label="Previous slide">&#8249;</button>
                <div className="artemis-lightbox__dots">
                  {slides.map((_, i) => (
                    <button key={i} className={`artemis-lightbox__dot${i === slideIndex ? ' artemis-lightbox__dot--active' : ''}`} onClick={() => { setSlideIndex(i); onInteract?.() }} aria-label={`Slide ${i + 1}`} />
                  ))}
                </div>
                <button className="artemis-lightbox__nav-btn" onClick={() => { playSound('DataPoint_Open'); setSlideIndex(i => Math.min(i + 1, slides.length - 1)); onInteract?.() }} disabled={slideIndex === slides.length - 1} aria-label="Next slide">&#8250;</button>
              </div>
            )}
          </div>
        </div>

        {/* Right: text + button */}
        <div className="artemis-lightbox__right">
          {media.label && (
            <div className={`artemis-lightbox__title${flicker}`}>{media.label}</div>
          )}
          <div className={`artemis-lightbox__body-wrap${flicker}`}>
            <SlideBody key={slideIndex} body={slide?.body} />
          </div>
          {!hideCloseButton && (
            <button className={`artemis-lightbox__close${flicker}`} onClick={() => { onClose() }}>
              <span>{guided ? 'CONTINUE' : 'CLOSE'}</span>
            </button>
          )}
        </div>

        </div>
      </div>
    </div>
  )
}
